import React, { useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { STORAGE } from "../../../../Config/firebase";

const MAX_DIM = 1200;
const MAX_BYTES = 1.5 * 1024 * 1024;

function uid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const ratio = Math.min(MAX_DIM / width, MAX_DIM / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const keepPng = file.type === "image/png";
      const type = keepPng ? "image/png" : "image/jpeg";
      const ext = keepPng ? "png" : "jpg";
      const quality = keepPng ? undefined : 0.86;
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          if (!blob) {
            reject(new Error("Could not process image"));
            return;
          }
          if (blob.size > MAX_BYTES) {
            if (keepPng) {
              reject(new Error("Image is larger than 1.5 MB after resize"));
              return;
            }
            canvas.toBlob(
              (smaller) => {
                if (!smaller || smaller.size > MAX_BYTES) {
                  reject(new Error("Image is larger than 1.5 MB after resize"));
                  return;
                }
                resolve({ blob: smaller, ext, type });
              },
              type,
              0.72
            );
            return;
          }
          resolve({ blob, ext, type });
        },
        type,
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not read image"));
    };
    img.src = objectUrl;
  });
}

const ImageUploadField = ({ value, onChange, label = "Image" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setError("");
    setUploading(true);
    try {
      const { blob, ext, type } = await resizeImage(file);
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const path = `email_assets/${yyyy}/${mm}/${uid()}.${ext}`;
      const storageRef = ref(STORAGE, path);
      await uploadBytes(storageRef, blob, { contentType: type });
      const url = await getDownloadURL(storageRef);
      onChange(url);
    } catch (e) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12, mb: 0.75 }}>{label}</Typography>
      {value ? (
        <Box
          component="img"
          src={value}
          alt=""
          sx={{
            display: "block",
            maxWidth: "100%",
            maxHeight: 140,
            borderRadius: 1,
            mb: 1,
            border: "1px solid rgba(255,255,255,0.1)",
          }}
        />
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          handleFile(file);
        }}
      />
      <Button
        size="small"
        variant="outlined"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        sx={{ borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.85)", textTransform: "none" }}
      >
        {uploading ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
        {uploading ? "Uploading…" : value ? "Replace image" : "Upload image"}
      </Button>
      {value ? (
        <Button size="small" onClick={() => onChange("")} sx={{ color: "rgba(255,255,255,0.55)", textTransform: "none", ml: 1 }}>
          Remove
        </Button>
      ) : null}
      {error ? (
        <Typography sx={{ color: "#F52A2D", fontSize: 12, mt: 0.75 }}>{error}</Typography>
      ) : (
        <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: 11, mt: 0.75 }}>
          JPEG or PNG, resized to 1200px. WebP converts to JPEG (Outlook). Max 1.5 MB.
        </Typography>
      )}
    </Box>
  );
};

export default ImageUploadField;
